// Busca por nome e telefone na agenda (A-3 item c) — pura.
//
// ★★★ POR QUE EM MEMÓRIA, e não uma consulta nova.
//
// A agenda já carregou os agendamentos da janela exibida, e cada documento
// DENORMALIZA `leadNome` e `leadTelefone` (`gradeVisao.js:montarCaixa`). Então
// não há leitura nova a fazer, não há índice composto a criar, e o recorte
// acompanha a navegação de data sem uma viagem ao servidor por tecla digitada.
//
// ⚠ A consequência honesta: a busca enxerga **só o período na tela**. Quem
// procurar alguém de outro mês não acha — e é por isso que o campo diz "neste
// período", em vez de prometer o que não cumpre.
//
// ★★ O TELEFONE CASA AS DUAS GRAFIAS, e isso não é detalhe de formatação: a
// agenda em papel do Careflow escreve muito celular SEM o nono dígito, enquanto
// quem digita na busca escreve COM. `variantesDaBusca` monta o E.164 a partir do
// texto solto e devolve as duas grafias (`lib/telefone.js`, a mesma função que a
// busca de conversas usa). Comparar só o que a pessoa digitou deixaria metade
// dos agendamentos importados inalcançável.

import { variantesDaBusca } from '@/lib/telefone';

/**
 * Texto comparável: minúsculo e SEM ACENTO.
 *
 * ★ Sem a normalização, procurar "jessica" não acharia "Jéssica" — e ninguém
 * digita acento numa caixa de busca com pressa.
 */
const dobrar = (t) =>
  String(t ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const digitos = (t) => String(t ?? '').replace(/\D/g, '');

/**
 * O que procurar, derivado UMA VEZ do termo — para não recalcular as variantes
 * de telefone a cada agendamento da lista.
 *
 * Devolve `null` quando o termo não busca nada (vazio ou só espaço): quem chama
 * usa isso para devolver a lista intacta, sem percorrer nada.
 */
export function prepararBusca(termo) {
  const texto = dobrar(termo);
  if (!texto) return null;
  const crus = digitos(termo);
  /**
   * ★★ DUAS FAIXAS DE TELEFONE, de propósito:
   *
   *   `variantes`  o número completo em E.164, nas duas grafias — é o que casa
   *                "62 99664-0861" com um `leadTelefone` gravado sem o 9;
   *   `parcial`    o PEDAÇO digitado (≥ 4 dígitos) — é o que faz "0861" achar
   *                alguém sem exigir que a pessoa lembre o DDD.
   *
   * ⚠ O piso de 4 não é estético: com 3 dígitos quase todo telefone casa, e a
   * busca devolveria a agenda inteira dizendo que filtrou.
   */
  return {
    texto,
    variantes: variantesDaBusca(termo).map(digitos).filter(Boolean),
    parcial: crus.length >= 4 ? crus : '',
  };
}

/** Um agendamento casa o termo já preparado? */
export function casaAgendamento(a, alvo) {
  if (!alvo) return true;
  if (dobrar(a?.leadNome).includes(alvo.texto)) return true;
  const tel = digitos(a?.leadTelefone);
  if (!tel) return false;
  if (alvo.variantes.some((v) => tel === v || tel.endsWith(v) || v.endsWith(tel))) return true;
  return Boolean(alvo.parcial) && tel.includes(alvo.parcial);
}

/**
 * A lista filtrada pelo termo. Termo vazio devolve a MESMA lista (identidade),
 * e isso é proposital: `montarGrade` roda dentro de um `useMemo` que depende da
 * lista, e criar um array novo a cada render recalcularia a grade sem motivo.
 */
export function filtrarAgendamentos(lista = [], termo = '') {
  const alvo = prepararBusca(termo);
  if (!alvo) return lista;
  return lista.filter((a) => casaAgendamento(a, alvo));
}
